import { FC } from "react";
import { colors, darken, lighten } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LabeledValue } from "../../../../datasources/responses/resultsOverview";
import CustomNoRowsOverlay from "../../../../features/results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import { useCurrentTheme } from "../../../../theme/theme";
import { ThemeColors } from "../../../../theme/themeValues";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

export type MultiValueData = {
	[key: string]: number | string;
};

interface Props {
	data: MultiValueData[] | LabeledValue[];
	measure: string;
	color?: string;
	labelFormatter?: (label: string) => string;
	valueFormatter?: (value: number) => string;
	isTabMode: boolean;
}

const SimpleBarChart: FC<Props> = ({ data, measure, color, labelFormatter, valueFormatter, isTabMode }) => {
	const { t } = useTranslation("feature.units");
	const { theme } = useCurrentTheme();

	const baseColor = color ?? ThemeColors.LightSecondary;

	/** numeric keys for multi-value mode */
	const numericKeys = Object.keys(data?.[0] || {}).filter(
		(key) => key !== "label" && typeof (data?.[0] as any)[key] === "number"
	);

	/** remove first key (total) */
	const keysWithoutTotal = numericKeys.filter((value) => value !== "value");

	/** normalize data */
	const formattedData = (data as MultiValueData[]).map((item) => {
		const record: Record<string, any> = { ...item, measure };

		if (labelFormatter) record.label = labelFormatter(String(item.label));

		if (valueFormatter) {
			for (const key of numericKeys) {
				const value = (item as Record<string, unknown>)[key];
				if (typeof value === "number") {
					record[key] = Number(valueFormatter(value));
				}
			}
		}

		return record;
	});

	const isMultiValue = numericKeys.length > 1;

	/** color generator */
	const getVariantColor = (baseColor: string, index: number): string => {
		if (index === 0) return baseColor;

		const step = 0.075;
		return index % 2 === 1
			? darken(baseColor, step * Math.ceil(index / 2))
			: lighten(baseColor, step * Math.ceil(index / 2));
	};

	/** legend payload (only non-total keys) */
	const legendPayload = keysWithoutTotal.map((key, index) => ({
		id: key,
		type: "square",
		value: t(`greenrouterDetail/legend/${key}`, key),
		color: getVariantColor(baseColor, index),
	}));

	if (formattedData.length === 0) {
		return (
			<ResponsiveContainer
				width="100%"
				height={isTabMode ? 400 : "100%"}
				style={{
					backgroundColor: theme.palette.background.default,
					padding: isTabMode ? 16 : 0,
				}}
			>
				<div
					style={{
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "#999",
						fontSize: 14,
						fontWeight: 500,
					}}
				>
					<CustomNoRowsOverlay />
				</div>
			</ResponsiveContainer>
		);
	}

	return (
		<ResponsiveContainer
			width="100%"
			height={isTabMode ? 400 : "100%"}
			style={{
				background: theme.palette.background.default,
				padding: isTabMode ? 16 : 0,
			}}
		>
			<BarChart data={formattedData} margin={{ top: 20, right: 20, left: 20, bottom: 70 }}>
				<CartesianGrid strokeDasharray="3 3" />
				<XAxis dataKey="label" tick={{ textAnchor: "end" }} angle={-60} fontSize={11} interval={0} />
				<YAxis fontSize={12} tickFormatter={(value) => new Intl.NumberFormat("it-IT").format(value)} />

				<Tooltip
					content={(props) => {
						const totalKey = numericKeys[0];
						const totalValue = props?.payload?.[0]?.payload?.[totalKey] ?? 0;
						return <CustomTooltip {...props} isMulti={isMultiValue} total={totalValue} />;
					}}
				/>

				{isMultiValue && (
					<Legend verticalAlign="top" align="center" content={<CustomLegend payload={legendPayload} />} />
				)}

				{!isMultiValue ? (
					<Bar
						dataKey={numericKeys[0] ?? "value"}
						fill={baseColor}
						name={t(`greenrouterDetail/legend/value`, "Value")}
					/>
				) : (
					keysWithoutTotal.map((key, index) => (
						<Bar
							key={key}
							dataKey={key}
							fill={getVariantColor(baseColor, index)}
							name={t(`greenrouterDetail/legend/${key}`, key)}
							stackId="multi"
						/>
					))
				)}
			</BarChart>
		</ResponsiveContainer>
	);
};

export default SimpleBarChart;
